import React from "react";

// Matches the various ways a stale/missing lazy chunk surfaces across browsers
// after a new frontend deploy (the old index.html points at hashes that no
// longer exist on the server).
const CHUNK_ERROR_RE =
  /(ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/i;

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = (error as { message?: string })?.message ?? String(error);
  const name = (error as { name?: string })?.name ?? "";
  return CHUNK_ERROR_RE.test(msg) || CHUNK_ERROR_RE.test(name);
}

type Props = {
  children: React.ReactNode;
  // When this changes (e.g. the route path) the boundary clears its error, so
  // navigating away from a crashed page recovers without a manual reload.
  resetKey?: string;
  // Show the raw error message on the card. Staff only — never clients.
  showDetails?: boolean;
};
type State = { error: Error | null; prevResetKey: string | undefined };

const RELOAD_TS_KEY = "mfx_chunk_reload_ts";

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, prevResetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.prevResetKey) {
      return { error: null, prevResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Stale chunk after a deploy: reload once to pull the new build. Guarded by
    // a timestamp so a persistent failure can't cause a reload loop.
    if (isChunkLoadError(error)) {
      const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || "0");
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <div className="max-w-md">
            <div className="text-4xl mb-3">⚠️</div>
            <h2 className="text-heading-md text-dark-800 mb-1">This page hit a snag</h2>
            <p className="text-body-sm text-dark-500 mb-4">
              Something went wrong rendering this page. Reloading usually fixes it — your work elsewhere is unaffected.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem(RELOAD_TS_KEY);
                window.location.reload();
              }}
              className="h-10 px-5 rounded-xl text-body-sm font-medium bg-brand-500 text-white hover:bg-brand-600 transition-colors"
            >
              Reload page
            </button>
            <p className="mt-4 text-2xs text-dark-400 break-words font-mono">
              {this.props.showDetails ? this.state.error.message : "If this keeps happening, let the team know."}
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
